import React from 'react';

import { Switch } from '@mui/material';
import { isNotNil } from 'es-toolkit';
import { useField, useFormState } from 'react-final-form';

import { EmployerSimpleInfoFragment } from 'src/api';
import { Tooltip } from 'src/components/common';

import { PeoLiveStatusFormField, ROW_LOCKED_TOOLTIP } from '../constants';
import { PeoLiveStatusValues } from '../types';

type PeoEmployersLiveStatusTableSwitchCellProps = {
  employer: EmployerSimpleInfoFragment;
};

export const PeoEmployersLiveStatusTableSwitchCell: React.FC<
  PeoEmployersLiveStatusTableSwitchCellProps
> = ({ employer }) => {
  const { input } = useField(`${PeoLiveStatusFormField.liveStatusChanges}.${employer.id}`);
  const { values, submitting } = useFormState<PeoLiveStatusValues>({
    subscription: { values: true, submitting: true },
  });

  const savedChecked = !!employer.setLiveAt;
  const hasBulkChange = isNotNil(values[PeoLiveStatusFormField.setAllLive]);

  const checked = React.useMemo(() => {
    // final-form drops the whole `liveStatusChanges` object once its last key is removed
    return values[PeoLiveStatusFormField.liveStatusChanges]?.[employer.id] ?? savedChecked;
  }, [employer.id, savedChecked, values]);

  const handleSwitchChange = React.useCallback(
    (_event: unknown, checked: boolean) => {
      // Toggling back to the saved value drops the change so the form is pristine again
      input.onChange(checked === savedChecked ? undefined : checked);
    },
    [input, savedChecked],
  );

  return (
    <Tooltip title={hasBulkChange ? ROW_LOCKED_TOOLTIP : ''} placement="left" arrow>
      {/* A disabled switch fires no events, so the wrapper is the tooltip anchor */}
      <span>
        <Switch
          checked={checked}
          disabled={submitting || hasBulkChange}
          onChange={handleSwitchChange}
          onBlur={input.onBlur}
          onFocus={input.onFocus}
        />
      </span>
    </Tooltip>
  );
};
